import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Edit3, Plus, Save, Search, Tag, Trash2, Upload } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import LabelEditor from '@/components/haccp/LabelEditor';
import PageHeader from '@/components/haccp/PageHeader';
import EmptyState from '@/components/haccp/EmptyState';
import ConfirmActionDialog from '@/components/haccp/ConfirmActionDialog';
import EntityBadge from '@/components/haccp/EntityBadge';
import { ErrorState, LoadingState } from '@/components/haccp/AsyncState';
import { parseLabelTemplateBundle } from '@/lib/labelTemplateImport';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_LAYOUT = {
  width_mm: 60, height_mm: 40,
  elements: [
    { id: 'e1', type: 'dynamic', field: 'article_name', x_mm: 3, y_mm: 3, w_mm: 54, h_mm: 5, font_size: 10, bold: true, align: 'left', fit_mode: 'shrink' },
    { id: 'e2', type: 'static', content: 'Lotto:', x_mm: 3, y_mm: 10, w_mm: 12, h_mm: 3, font_size: 6, bold: true, align: 'left', fit_mode: 'shrink' },
    { id: 'e3', type: 'dynamic', field: 'lot_number', x_mm: 15, y_mm: 10, w_mm: 42, h_mm: 3, font_size: 6, bold: false, align: 'left', fit_mode: 'shrink' },
    { id: 'e4', type: 'static', content: 'Prod.:', x_mm: 3, y_mm: 14, w_mm: 12, h_mm: 3, font_size: 6, bold: true, align: 'left', fit_mode: 'shrink' },
    { id: 'e5', type: 'dynamic', field: 'production_date', x_mm: 15, y_mm: 14, w_mm: 42, h_mm: 3, font_size: 6, bold: false, align: 'left', fit_mode: 'shrink' },
    { id: 'e6', type: 'static', content: 'Scad.:', x_mm: 3, y_mm: 18, w_mm: 12, h_mm: 3, font_size: 6, bold: true, align: 'left', fit_mode: 'shrink' },
    { id: 'e7', type: 'dynamic', field: 'expiry_date', x_mm: 15, y_mm: 18, w_mm: 42, h_mm: 3, font_size: 6, bold: false, align: 'left', fit_mode: 'shrink' },
    { id: 'e8', type: 'dynamic', field: 'allergens', x_mm: 3, y_mm: 24, w_mm: 39, h_mm: 6, font_size: 5, bold: true, align: 'left', color: 'red', fit_mode: 'shrink' },
    { id: 'e9', type: 'dynamic', field: 'company', x_mm: 3, y_mm: 32, w_mm: 39, h_mm: 5, font_size: 5, bold: false, align: 'left', fit_mode: 'shrink' },
    { id: 'e10', type: 'qr', field: 'qr_payload', x_mm: 45, y_mm: 24, w_mm: 12, h_mm: 12 },
  ]
};

const formatDateTime = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
};

const formatPrintSize = label => {
  const settings = label.print_settings || {};
  const width = settings.paper_width_mm;
  const height = settings.paper_height_mm;
  return width && height ? `${width}×${height} mm` : '—';
};

export default function EtichettePage() {
  const [tab, setTab] = useState('templates');
  const [labels, setLabels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [initialDraft, setInitialDraft] = useState('');
  const [exitConfirm, setExitConfirm] = useState(false);
  const [editingTpl, setEditingTpl] = useState(null);
  const [tplName, setTplName] = useState('');
  const [tplLayout, setTplLayout] = useState(DEFAULT_LAYOUT);
  const [designOnly, setDesignOnly] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const templateFileInput = useRef(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [importingTemplates, setImportingTemplates] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [lr, tr] = await Promise.all([
        fetch(`${BACKEND_URL}/api/labels/`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/label-templates/`, { credentials: 'include' }),
      ]);
      if (!lr.ok || !tr.ok) throw new Error('Impossibile caricare storico e template');
      const [la, te] = await Promise.all([lr.json(), tr.json()]);
      setLabels(la);
      setTemplates(te);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const templateById = useMemo(() => new Map(templates.map(t => [t.template_id, t])), [templates]);
  const printedLabels = useMemo(() => labels.filter(l => l.print_job_id && l.print_method), [labels]);
  const visibleLabels = useMemo(() => {
    const q = search.trim().toLowerCase();
    return printedLabels.filter(l => !q || [
      l.product_name,
      l.article_name,
      l.commercial_name,
      l.lot_number,
      templateById.get(l.template_id)?.name,
    ].some(v => String(v || '').toLowerCase().includes(q)));
  }, [printedLabels, search, templateById]);
  const visibleTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter(t => !q || String(t.name || '').toLowerCase().includes(q));
  }, [templates, search]);

  const openNewTpl = () => {
    setEditingTpl(null);
    setTplName('');
    setTplLayout(DEFAULT_LAYOUT);
    setDesignOnly(false);
    setInitialDraft(JSON.stringify({ name: '', layout: DEFAULT_LAYOUT }));
    setEditorOpen(true);
  };
  const openEditTpl = t => {
    setEditingTpl(t);
    setTplName(t.name);
    setTplLayout(t.layout || DEFAULT_LAYOUT);
    setDesignOnly(Boolean(t.design_only));
    setInitialDraft(JSON.stringify({ name: t.name, layout: t.layout || DEFAULT_LAYOUT }));
    setEditorOpen(true);
  };
  const closeEditor = () => {
    if (JSON.stringify({ name: tplName, layout: tplLayout }) !== initialDraft) setExitConfirm(true);
    else setEditorOpen(false);
  };
  const saveTemplate = async () => {
    if (!tplName.trim()) return toast.error('Nome richiesto');
    const fields = {};
    (tplLayout.elements || []).forEach(el => {
      if (el.type === 'dynamic') fields[el.field] = true;
      if (el.type === 'qr') fields.qr_payload = true;
    });
    const r = await fetch(editingTpl ? `${BACKEND_URL}/api/label-templates/${editingTpl.template_id}` : `${BACKEND_URL}/api/label-templates/`, {
      method: editingTpl ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name: tplName.trim(), layout: tplLayout, fields, ...(!editingTpl ? { design_only: designOnly } : {}) })
    });
    if (!r.ok) return toast.error('Errore salvataggio template');
    toast.success(editingTpl ? 'Template aggiornato' : 'Template creato');
    setEditorOpen(false);
    await load();
  };
  const importTemplates = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || importingTemplates) return;
    if (!file.name.toLowerCase().endsWith('.json')) return toast.error('Seleziona un file template .json');
    if (file.size > 2 * 1024 * 1024) return toast.error('Il file supera il limite di 2 MB');

    setImportingTemplates(true);
    let created = 0;
    let skipped = 0;
    try {
      const imported = parseLabelTemplateBundle(await file.text());
      const existingNames = new Set(templates.map(template => String(template.name || '').trim().toLocaleLowerCase('it-IT')));
      const pending = imported.filter(template => {
        if (existingNames.has(template.name.toLocaleLowerCase('it-IT'))) { skipped += 1; return false; }
        existingNames.add(template.name.toLocaleLowerCase('it-IT'));
        return true;
      });

      if (!pending.length) {
        toast.success(`${skipped} template già presenti; nessuna copia aggiunta`);
        return;
      }

      for (const template of pending) {
        const response = await fetch(`${BACKEND_URL}/api/label-templates/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(template),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.detail || `Errore caricando “${template.name}”`);
        created += 1;
      }
      toast.success(`${created} template caricati${skipped ? ` · ${skipped} già presenti` : ''}`);
    } catch (e) {
      toast.error(created
        ? `Importazione parziale: ${created} template caricati. ${e.message}`
        : e.message || 'Impossibile caricare il file template');
    } finally {
      setImportingTemplates(false);
      await load();
    }
  };
  const deleteTemplate = async () => {
    if (!deleteTarget) return;
    const r = await fetch(`${BACKEND_URL}/api/label-templates/${deleteTarget.template_id}`, { method: 'DELETE', credentials: 'include' });
    if (!r.ok) return toast.error('Impossibile eliminare il template');
    setDeleteTarget(null);
    toast.success('Template eliminato');
    await load();
  };

  return <div data-testid="etichette-page" className="app-page"><div className="app-page-inner">
    {!editorOpen && <PageHeader icon={Tag} title="Etichette" description="Gestisci i template e consulta lo storico delle etichette stampate." action={tab === 'templates' ? <div className="flex flex-wrap gap-2"><input ref={templateFileInput} type="file" accept=".json,application/json" className="hidden" aria-label="File template JSON" data-testid="template-import-input" onChange={importTemplates} /><Button onClick={() => templateFileInput.current?.click()} disabled={importingTemplates} variant="outline"><Upload />{importingTemplates ? 'Caricamento…' : 'Carica template'}</Button><Button onClick={openNewTpl} disabled={importingTemplates} variant="success"><Plus />Nuovo template</Button></div> : null} />}
    {editorOpen && <div className="surface p-3 space-y-2"><div className="flex justify-between items-center"><Button variant="outline" onClick={closeEditor}>← Template</Button><span className="font-semibold">{editingTpl ? 'Modifica template' : 'Nuovo template'}</span><Button onClick={saveTemplate}><Save />Salva</Button></div>{designOnly&&<p role="status" className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">Bozza progettuale: questo template è incompleto e non può essere stampato.</p>}<LabelEditor value={tplLayout} onChange={setTplLayout} name={tplName} onNameChange={setTplName} /></div>}

    {!editorOpen && <>
      <div className="surface flex gap-1 px-2 overflow-x-auto">
        {[['templates', 'Template'], ['history', 'Storico stampe']].map(([value, label]) => <button key={value} onClick={() => { setTab(value); setSearch(''); }} className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap ${tab === value ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>{label}</button>)}
      </div>

      {tab === 'templates' && <>
        <div className="toolbar"><div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input className="pl-9" value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca template" /></div></div>
        {loading ? <LoadingState /> : error ? <ErrorState description={error} onRetry={load} /> : visibleTemplates.length === 0 ? <EmptyState icon={Tag} title="Nessun template" description="Carica un file JSON di template oppure crea un template personalizzato." actionLabel={!search ? 'Nuovo template' : undefined} onAction={!search ? openNewTpl : undefined} /> : <div className="surface">{visibleTemplates.map(t => <div key={t.template_id} className="p-4 border-b last:border-b-0 flex items-center justify-between gap-3 flex-wrap"><div><p className="font-medium">{t.name}{t.design_only&&<span className="ml-2 text-xs font-semibold text-amber-800 bg-amber-100 rounded px-2 py-1">BOZZA · NON STAMPABILE</span>}</p><p className="text-xs text-muted-foreground">{t.layout?.width_mm || 60}×{t.layout?.height_mm || 40} mm · {t.layout?.elements?.length || 0} elementi</p></div><div className="flex gap-2"><Button size="sm" variant="outline" className="action-edit" onClick={() => openEditTpl(t)}><Edit3 />Modifica</Button><Button size="icon" variant="ghost" className="text-red-600" aria-label={`Elimina ${t.name}`} onClick={() => setDeleteTarget(t)}><Trash2 /></Button></div></div>)}</div>}
      </>}

      {tab === 'history' && <>
        <div className="toolbar"><div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input className="pl-9" value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca prodotto, lotto o template" /></div></div>
        {loading ? <LoadingState /> : error ? <ErrorState description={error} onRetry={load} /> : visibleLabels.length === 0 ? <EmptyState icon={Tag} title="Nessuna stampa" description={search ? 'Nessuna stampa corrisponde alla ricerca.' : 'Le etichette effettivamente stampate appariranno qui.'} /> : <div className="surface overflow-hidden">
          <div className="hidden lg:grid lg:grid-cols-[150px_minmax(180px,1.4fr)_190px_minmax(160px,1fr)_130px_70px] gap-3 px-4 py-3 bg-slate-50 border-b text-xs font-semibold uppercase tracking-wide text-slate-500"><span>Data</span><span>Prodotto</span><span>Lotto</span><span>Template</span><span>Formato</span><span>Copie</span></div>
          {visibleLabels.map(l => <div key={l.label_id} className="grid gap-2 px-4 py-3 border-b last:border-b-0 lg:grid-cols-[150px_minmax(180px,1.4fr)_190px_minmax(160px,1fr)_130px_70px] lg:gap-3 lg:items-center">
            <div className="text-sm"><span className="lg:hidden text-xs text-muted-foreground mr-2">Data</span>{formatDateTime(l.created_at)}</div>
            <div className="min-w-0"><span className="lg:hidden block text-xs text-muted-foreground">Prodotto</span><p className="font-medium truncate">{l.product_name || l.commercial_name || l.article_name || '—'}</p></div>
            <div><span className="lg:hidden block text-xs text-muted-foreground mb-1">Lotto</span><EntityBadge entity="lot" label={l.lot_number} compact /></div>
            <div className="min-w-0"><span className="lg:hidden block text-xs text-muted-foreground">Template</span><p className="text-sm truncate">{templateById.get(l.template_id)?.name || 'Template non disponibile'}</p></div>
            <div className="text-sm"><span className="lg:hidden text-xs text-muted-foreground mr-2">Formato</span>{formatPrintSize(l)}</div>
            <div className="text-sm font-medium"><span className="lg:hidden text-xs font-normal text-muted-foreground mr-2">Copie</span>{l.print_settings?.copies || 1}</div>
          </div>)}
        </div>}
      </>}
    </>}

    <ConfirmActionDialog open={exitConfirm} onOpenChange={setExitConfirm} title="Uscire senza salvare?" description="Le modifiche al template andranno perse." confirmLabel="Esci" onConfirm={() => { setExitConfirm(false); setEditorOpen(false); }} />
    <ConfirmActionDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)} title="Eliminare il template?" description={`Il template ${deleteTarget?.name || ''} non sarà più disponibile.`} confirmLabel="Elimina" onConfirm={deleteTemplate} />
  </div></div>;
}
